import { useState, useEffect, useCallback, useRef } from 'react';

interface Heart {
  id:    string;
  x:     number;
  size:  number;
  color: string;
  dur:   number; // ms
}

const CORES = [
  'rgba(251,113,133,0.80)',
  'rgba(244,114,182,0.75)',
  'rgba(244,63,94,0.85)',
  'rgba(236,72,153,0.80)',
  'rgba(253,164,175,0.90)',
  'rgba(248,113,113,0.80)',
  'rgba(190,18,60,0.90)',
];

const TAMANHOS = [16, 20, 22, 24, 28];

function novoCoracao(): Heart {
  return {
    id:    Math.random().toString(36).slice(2),
    x:     3 + Math.random() * 94,
    size:  TAMANHOS[Math.floor(Math.random() * TAMANHOS.length)],
    color: CORES[Math.floor(Math.random() * CORES.length)],
    dur:   7000 + Math.floor(Math.random() * 5000),
  };
}

export default function TvDiaNamoradosTakeover({ hideBadge = false }: { hideBadge?: boolean }) {
  const [hearts, setHearts] = useState<Heart[]>([]);
  const heartTimersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  const spawn = useCallback(() => {
    const h = novoCoracao();
    setHearts(prev => [...prev, h]);
    // remove após a animação terminar (+ margem de 300ms)
    const t = setTimeout(() => {
      heartTimersRef.current.delete(t);
      setHearts(prev => prev.filter(c => c.id !== h.id));
    }, h.dur + 300);
    heartTimersRef.current.add(t);
  }, []);

  useEffect(() => {
    spawn(); // primeiro coração imediato
    const interval = setInterval(spawn, 900);
    return () => {
      clearInterval(interval);
      heartTimersRef.current.forEach(clearTimeout);
      heartTimersRef.current.clear();
    };
  }, [spawn]);

  return (
    <>
      {/* Overlay estático: vinheta + bordas + badge */}
      <div className="fixed inset-0 z-[14] pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            background: [
              'radial-gradient(ellipse at 0% 0%,    rgba(244,63,94,0.22) 0%, transparent 42%)',
              'radial-gradient(ellipse at 100% 0%,  rgba(244,63,94,0.16) 0%, transparent 38%)',
              'radial-gradient(ellipse at 50% 100%, rgba(236,72,153,0.20) 0%, transparent 42%)',
              'radial-gradient(ellipse at 100% 100%, rgba(244,63,94,0.16) 0%, transparent 38%)',
            ].join(', '),
          }}
        />
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-rose-500/70 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-pink-500/70 to-transparent" />

        {!hideBadge && (
          <div
            className="absolute bottom-6 right-6 flex items-center gap-1.5 rounded-full px-3 py-1.5 backdrop-blur-sm shadow-lg"
            style={{ background: 'rgba(136,19,55,0.65)', border: '1px solid rgba(244,63,94,0.35)' }}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="rgba(253,164,175,1)">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
            <span className="text-xs font-bold text-rose-300 tracking-wide">Dia dos Namorados</span>
          </div>
        )}
      </div>

      {/* Corações — fixed direto no viewport (sem overflow:hidden bloqueando o transform)
          Mesmo padrão de TvVibeTakeover: position:fixed + animationDuration inline */}
      {hearts.map(h => (
        <div
          key={h.id}
          className="fixed bottom-0 z-[14] pointer-events-none select-none animate-tema-coracao-subir"
          style={{
            left:              `${h.x}%`,
            animationDuration: `${h.dur}ms`,
            willChange:        'transform',
          }}
        >
          <svg width={h.size} height={h.size} viewBox="0 0 24 24" fill={h.color}>
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
          </svg>
        </div>
      ))}
    </>
  );
}
